import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { submitContactRequest } from '../lib/contactApi';

type KontaktFormData = {
  name: string;
  email: string;
  phone?: string;
  message: string;
};

export const Kontakt = () => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<KontaktFormData>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      message: ''
    }
  });

  const onSubmit = async (data: KontaktFormData) => {
    setSubmitError(null);
    const messageLines = [
      `Telefon: ${data.phone || '-'}`,
      '',
      'Nachricht:',
      data.message
    ];
    try {
      await submitContactRequest({
        name: data.name,
        email: data.email,
        subject: 'Kontaktanfrage (Website)',
        message: messageLines.join('\n')
      });
      reset();
      navigate('/kontakt-erfolgreich');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Senden fehlgeschlagen.');
    }
  };

  const copyToClipboard = async (label: string, value: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      toast.success(`${label} kopiert`);
    } catch (error) {
      toast.error('Kopieren fehlgeschlagen.');
    }
  };

  const address = `Staisch Innovative Elektrotechnik\nFreiherr-vom-Stein-Straße 14\n72654 Neckartenzlingen`;
  const phone = '01575 8166291';
  const email = 'info@staisch-elektrotechnik.de';
  const nameErrorId = 'kontakt-name-error';
  const emailErrorId = 'kontakt-email-error';
  const messageErrorId = 'kontakt-message-error';

  return (
    <>
      <section className="page-hero" id="top">
        <div className="container page-hero__inner">
          <div>
            <span className="section__eyebrow">Kontakt</span>
            <h1>Ihr direkter Draht zu Staisch Innovative Elektrotechnik</h1>
            <p className="page-hero__lead">
              Sie haben Fragen zu Elektroinstallationen, Netzwerktechnik oder
              Sicherheitssystemen im Raum Stuttgart und Neckartenzlingen? Melden Sie
              sich telefonisch oder per E-Mail – wir antworten zeitnah.
            </p>
          </div>
          <a className="btn btn--primary" href="tel:+4915758166291">
            Jetzt anrufen
          </a>
        </div>
      </section>

      <section className="section section--contact">
        <div className="container contact">
          <div className="contact__details">
            <div className="contact__info">
              <p>
                <strong>Adresse</strong>
                <br />
                <button
                  className="contact__copy"
                  type="button"
                  onClick={() => copyToClipboard('Adresse', address)}
                  aria-label="Adresse kopieren"
                >
                  <span>Staisch Innovative Elektrotechnik</span>
                  <span>Freiherr-vom-Stein-Straße 14</span>
                  <span>72654 Neckartenzlingen</span>
                </button>
              </p>
              <p>
                <strong>Telefon</strong>
                <br />
                <button
                  className="contact__copy contact__copy--inline"
                  type="button"
                  onClick={() => copyToClipboard('Telefonnummer', phone)}
                  aria-label={`Telefonnummer kopieren: ${phone}`}
                >
                  {phone}
                </button>
              </p>
              <p>
                <strong>E-Mail</strong>
                <br />
                <button
                  className="contact__copy contact__copy--inline"
                  type="button"
                  onClick={() => copyToClipboard('E-Mail-Adresse', email)}
                  aria-label={`E-Mail-Adresse kopieren: ${email}`}
                >
                  {email}
                </button>
              </p>
            </div>
            <div className="contact__hours">
              <h2>Termine</h2>
              <p>Ortstermine und Serviceeinsätze vereinbaren wir individuell nach Absprache.</p>
            </div>
            <div className="contact__map">
              <iframe
                title="Lage von Staisch Innovative Elektrotechnik"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2667.385664268305!2d11.240433!3d48.177377!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479dde141c0eb1f1%3A0xa1983251ccc4293e!2sAuguste-Kent-Platz%202%2C%2082256%20F%C3%BCrstenfeldbruck!5e0!3m2!1sde!2sde!4v1710758400000"
              />
            </div>
          </div>
          <form className="contact__form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <h2>Anfrage</h2>
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                id="name"
                type="text"
                placeholder="Ihr Name"
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? nameErrorId : undefined}
                {...register('name', { required: 'Bitte geben Sie Ihren Namen ein.' })}
              />
              {errors.name && (
                <p id={nameErrorId} className="form-error" role="alert">
                  {errors.name.message}
                </p>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="email">E-Mail</label>
              <input
                id="email"
                type="email"
                placeholder="Ihre E-Mail-Adresse"
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? emailErrorId : undefined}
                {...register('email', {
                  required: 'Bitte geben Sie Ihre E-Mail-Adresse ein.',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.'
                  }
                })}
              />
              {errors.email && (
                <p id={emailErrorId} className="form-error" role="alert">
                  {errors.email.message}
                </p>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="phone">Telefon</label>
              <input
                id="phone"
                type="tel"
                placeholder="Ihre Telefonnummer (optional)"
                {...register('phone')}
              />
            </div>
            <div className="form-group">
              <label htmlFor="message">Anliegen</label>
              <textarea
                id="message"
                rows={5}
                placeholder="Worum geht es?"
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? messageErrorId : undefined}
                {...register('message', {
                  required: 'Bitte schildern Sie kurz Ihr Anliegen.'
                })}
              />
              {errors.message && (
                <p id={messageErrorId} className="form-error" role="alert">
                  {errors.message.message}
                </p>
              )}
            </div>
            <button className="btn btn--primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Wird gesendet…' : 'Nachricht senden'}
            </button>
            <p className="contact__privacy">
              Mit dem Absenden akzeptieren Sie unsere <Link to="/datenschutz">Datenschutzhinweise</Link>.
            </p>
            {submitError && (
              <p className="form-error" role="alert">
                {submitError}
              </p>
            )}
          </form>
        </div>
      </section>
    </>
  );
};
